import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, MapPin, Package, Motorcycle, ForkKnife, Plus, CreditCard, ShieldCheck, Lock, Coins, Wallet as WalletIcon } from '@phosphor-icons/react';
import { placeOrder, getProfile, addAddress, updateAddressLocation, createPaymentOrder, verifyPayment, getMyWallet, getWalletDashboard } from '../../services/customerApi';
import AddressLocationPicker from '@/components/Customer/AddressLocationPicker';
import GooglePlacesAddressInput from '@/components/Customer/GooglePlacesAddressInput';
import { getQuote } from '@/services/catalog';
import * as engine from '@/lib/ordering/cartEngine';
import { formatINR } from '@/lib/money';
import { useCustomer, PageWrap } from './CustomerApp';
import { toast } from 'sonner';

export default function CustomerCheckout() {
  const navigate = useNavigate();
  const { cart, selectedStore, clearCart, customer } = useCustomer();
  const [orderType, setOrderType] = useState('DELIVERY');
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [newAddress, setNewAddress] = useState({ label: 'Home', address_line: '', landmark: '', city: '', pincode: '' });
  const [googlePlaceLatLng, setGooglePlaceLatLng] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [payWithPoints, setPayWithPoints] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [useWallet, setUseWallet] = useState(false);

  const subtotal = engine.computeTotals(cart).subtotal;
  const needsLocationConfirm = orderType === 'DELIVERY' && !!selectedAddress && (selectedAddress.lat == null || selectedAddress.lng == null);
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    loadProfile();
    getMyWallet().then((res) => setWallet(res.data)).catch(() => {});
    getWalletDashboard().then((res) => setWalletBalance(res.data.wallet_balance || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedStore?.id || cart.length === 0) { setQuote(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const items = cart.map((c) => ({ menu_item_id: c.menu_item_id, qty: c.qty, variant: c.variant, modifiers: c.modifiers }));
        const quoteBody = { order_type: orderType, channel: 'online', items };
        if (orderType === 'DELIVERY' && selectedAddress?.lat != null && selectedAddress?.lng != null) {
          quoteBody.lat = selectedAddress.lat;
          quoteBody.lng = selectedAddress.lng;
        }
        const res = await getQuote(selectedStore.id, quoteBody);
        if (!cancelled) {
          setQuote(res.data);
          if (orderType === 'DELIVERY' && res.data?.delivery?.deliverable === false) {
            toast.error(res.data.delivery.reason || "Sorry, we don't deliver to this address", {
              description: 'Please choose a different address, or switch to Takeaway.',
              duration: 6000,
            });
          }
        }
      } catch { if (!cancelled) setQuote(null); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [orderType, cart, selectedStore, selectedAddress]);

  const loadProfile = async () => {
    try {
      const res = await getProfile();
      const addrs = res.data.addresses || [];
      setAddresses(addrs);
      if (addrs.length > 0) {
        const def = addrs.find((a) => a.is_default) || addrs[0];
        setSelectedAddress(def);
      }
    } catch (err) {
      // Ignore
    }
  };

  const handleProceedToLocation = () => {
    if (!newAddress.address_line.trim()) {
      toast.error('Enter an address');
      return;
    }
    // Google's own selection is already authoritative — no need to ask
    // "are you at this address" or drag a pin, that was only ever a
    // workaround for free-text addresses with no real geocoding behind them.
    if (googlePlaceLatLng) {
      finalizeAddAddress(googlePlaceLatLng.lat, googlePlaceLatLng.lng);
      return;
    }
    setShowLocationPicker(true);
  };

  const finalizeAddAddress = async (lat, lng) => {
    try {
      const payload = { ...newAddress, is_default: addresses.length === 0 };
      if (lat != null && lng != null) {
        payload.lat = lat;
        payload.lng = lng;
      }
      const res = await addAddress(payload);
      const addr = res.data.address;
      setAddresses((prev) => [...prev, addr]);
      setSelectedAddress(addr);
      setShowAddAddress(false);
      setShowLocationPicker(false);
      setNewAddress({ label: 'Home', address_line: '', landmark: '', city: '', pincode: '' });
      setGooglePlaceLatLng(null);
      toast.success(lat != null ? 'Address added with location' : 'Address added');
    } catch (err) {
      toast.error('Failed to add address');
    }
  };

  const handleConfirmExistingAddressLocation = async (lat, lng) => {
    if (!selectedAddress) return;
    try {
      await updateAddressLocation(selectedAddress.id, lat, lng);
      const updated = { ...selectedAddress, lat, lng };
      setAddresses((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      setSelectedAddress(updated);
      toast.success('Address location confirmed — saved for future orders too');
    } catch {
      toast.error('Failed to save location, please try again');
    }
  };

  const openRazorpay = (razorpayOrderId, amount, keyId, orderId) => {
    return new Promise((resolve, reject) => {
      const options = {
        key: keyId,
        amount: amount,
        currency: 'INR',
        name: 'SSPICE Pizza',
        description: `Order from ${selectedStore?.name || 'SSPICE'}`,
        order_id: razorpayOrderId,
        handler: (response) => {
          resolve({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
        },
        modal: {
          ondismiss: () => reject(new Error('Payment cancelled')),
        },
        prefill: {
          name: customer?.name || '',
          contact: customer?.phone || '',
          email: customer?.email || '',
        },
        theme: {
          color: '#E21836',
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response) => {
        reject(new Error(response.error?.description || 'Payment failed'));
      });
      rzp.open();
    });
  };

  const handlePlaceOrder = async () => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    if (orderType === 'DELIVERY' && !selectedAddress) {
      toast.error('Select a delivery address');
      return;
    }

    setLoading(true);
    try {
      // Step 1: Create order
      const orderData = {
        store_id: selectedStore.id,
        order_type: orderType,
        items: cart.map((item) => ({
          menu_item_id: item.menu_item_id,
          qty: item.qty,
          notes: item.notes || '',
          variant: item.variant || null,
          modifiers: (item.modifiers || []).map((m) => ({ id: m.id, name: m.name, price: m.price })),
        })),
        special_instructions: specialInstructions,
        payment_method: payWithPoints ? 'WALLET_POINTS' : (useWallet ? 'WALLET_RAZORPAY' : 'RAZORPAY'),
        pay_with_points: payWithPoints,
        use_wallet: useWallet && !payWithPoints,
        delivery_address: orderType === 'DELIVERY' && selectedAddress ? {
          address_line: selectedAddress.address_line,
          landmark: selectedAddress.landmark,
          city: selectedAddress.city,
          pincode: selectedAddress.pincode,
          lat: selectedAddress.lat ?? null,
          lng: selectedAddress.lng ?? null,
        } : null,
      };

      const orderRes = await placeOrder(orderData);
      const { id: orderId, total, order_number, payment_status, wallet_amount_used } = orderRes.data.order;

      // Points orders, and orders fully covered by wallet credit, are
      // already marked PAID by the backend the moment redemption/debit
      // succeeds — no Razorpay involved at all for either path.
      if (payWithPoints || payment_status === 'PAID') {
        clearCart();
        toast.success(
          payWithPoints
            ? `Order #${order_number} placed using your points!`
            : `Order #${order_number} placed using your wallet credit!`
        );
        navigate(`/order/track/${orderId}`);
        return;
      }

      // Step 2: Create Razorpay payment order — for the remaining amount
      // only, if a partial wallet amount was earmarked at checkout.
      const amountToCharge = useWallet ? Math.max(0, total - (wallet_amount_used || 0)) : total;
      const payRes = await createPaymentOrder(orderId, amountToCharge);
      const { razorpay_order_id, amount, key_id } = payRes.data;

      // Step 3: Open Razorpay checkout
      const paymentResult = await openRazorpay(razorpay_order_id, amount, key_id, orderId);

      // Step 4: Verify payment on backend
      await verifyPayment({
        order_id: orderId,
        ...paymentResult,
      });

      clearCart();
      toast.success(`Order #${order_number} placed & paid!`);
      navigate(`/order/track/${orderId}`);
    } catch (err) {
      if (err.message === 'Payment cancelled') {
        toast.error('Payment was cancelled. Your order is saved — you can retry.');
      } else {
        toast.error(err.response?.data?.detail || err.message || 'Order failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const orderTypes = [
    { type: 'DELIVERY', icon: Motorcycle, label: 'Delivery' },
    { type: 'TAKEAWAY', icon: Package, label: 'Takeaway' },
    { type: 'DINE_IN', icon: ForkKnife, label: 'Dine In' },
  ];

  useEffect(() => {
    if (cart.length === 0) navigate('/order/cart');
  }, [cart.length, navigate]);

  if (cart.length === 0) return null;

  return (
    <PageWrap>
      <div className="min-h-screen pb-36" data-testid="checkout-page">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-[#FFFFFF]/80 backdrop-blur-xl border-b border-[#E5E7EB]/50 px-4 py-3">
          <div className="flex items-center gap-3">
            <button data-testid="checkout-back-btn" onClick={() => navigate(-1)} className="p-1">
              <ArrowLeft size={20} className="text-[#111827]" />
            </button>
            <h2 className="text-lg font-bold text-[#111827]">Checkout</h2>
          </div>
        </div>

        <div className="px-4 pt-4 space-y-4">
          {/* Ordering from store */}
          <div className="flex items-center justify-between gap-2 bg-[#E21836]/5 border border-[#E21836]/20 rounded-2xl px-4 py-3" data-testid="checkout-store-banner">
            <div className="flex items-start gap-2 min-w-0">
              <MapPin size={18} weight="fill" className="text-[#E21836] mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-[10px] font-semibold text-[#E21836] uppercase tracking-wide">Ordering from</p>
                <p className="text-sm font-bold text-[#111827] truncate">{selectedStore?.name || 'No store selected'}</p>
                {selectedStore?.address && (
                  <p className="text-xs text-[#6B7280] truncate">{selectedStore.address}</p>
                )}
              </div>
            </div>
            <button
              data-testid="checkout-change-store-btn"
              onClick={() => navigate('/order/select-store')}
              className="flex-shrink-0 px-3 py-1.5 bg-white border border-[#E21836]/30 text-[#E21836] text-xs font-semibold rounded-full hover:bg-[#E21836]/10 transition-colors"
            >
              Change
            </button>
          </div>

          {/* Order Type */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-2">Order Type</h3>
            <div className="grid grid-cols-3 gap-2">
              {orderTypes.map(({ type, icon: Icon, label }) => (
                <button
                  key={type}
                  data-testid={`order-type-${type.toLowerCase()}`}
                  onClick={() => setOrderType(type)}
                  className={`flex flex-col items-center gap-1 py-3 rounded-xl border transition-all duration-200 ${
                    orderType === type
                      ? 'bg-[#E21836] text-white border-[#E21836]'
                      : 'bg-[#F8FAFC] text-[#6B7280] border-[#E5E7EB]'
                  }`}
                >
                  <Icon size={22} weight={orderType === type ? 'fill' : 'duotone'} />
                  <span className="text-xs font-semibold">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Delivery Address */}
          {orderType === 'DELIVERY' && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-2">Delivery Address</h3>
              {addresses.length > 0 ? (
                <div className="space-y-2">
                  {addresses.map((addr) => (
                    <button
                      key={addr.id}
                      data-testid={`address-${addr.id}`}
                      onClick={() => setSelectedAddress(addr)}
                      className={`w-full text-left p-3 rounded-xl border transition-all ${
                        selectedAddress?.id === addr.id
                          ? 'border-[#E21836] bg-[#E21836]/5'
                          : 'border-[#E5E7EB] bg-[#F8FAFC]'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <MapPin size={16} weight="duotone" className={selectedAddress?.id === addr.id ? 'text-[#E21836]' : 'text-[#6B7280]'} />
                        <div>
                          <span className="text-xs font-semibold text-[#111827]">{addr.label}</span>
                          <p className="text-xs text-[#6B7280] mt-0.5">{addr.address_line}</p>
                          {addr.landmark && <p className="text-xs text-[#6B7280]">{addr.landmark}</p>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#6B7280] mb-2">No saved addresses</p>
              )}
              <button
                data-testid="add-address-btn"
                onClick={() => setShowAddAddress(!showAddAddress)}
                className="mt-2 flex items-center gap-1 text-xs font-semibold text-[#E21836]"
              >
                <Plus size={14} weight="bold" /> Add New Address
              </button>

              {showAddAddress && !showLocationPicker && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-3 space-y-2">
                  <div className="flex gap-2">
                    {['Home', 'Work', 'Other'].map((lbl) => (
                      <button
                        key={lbl}
                        onClick={() => setNewAddress((p) => ({ ...p, label: lbl }))}
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          newAddress.label === lbl ? 'bg-[#E21836] text-white' : 'bg-[#F8FAFC] text-[#6B7280] border border-[#E5E7EB]'
                        }`}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                  <GooglePlacesAddressInput
                    value={newAddress.address_line}
                    onChange={(text) => {
                      setNewAddress((p) => ({ ...p, address_line: text }));
                      setGooglePlaceLatLng(null); // manual edit invalidates any prior Google selection
                    }}
                    onPlaceSelected={({ formattedAddress, lat, lng }) => {
                      setNewAddress((p) => ({ ...p, address_line: formattedAddress }));
                      setGooglePlaceLatLng({ lat, lng });
                    }}
                    placeholder="Start typing your full address..."
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      placeholder="Landmark"
                      value={newAddress.landmark}
                      onChange={(e) => setNewAddress((p) => ({ ...p, landmark: e.target.value }))}
                      className="px-3 py-2.5 bg-[#FFFFFF] border border-[#E5E7EB] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E21836]/20"
                    />
                    <input
                      placeholder="Pincode"
                      value={newAddress.pincode}
                      onChange={(e) => setNewAddress((p) => ({ ...p, pincode: e.target.value }))}
                      className="px-3 py-2.5 bg-[#FFFFFF] border border-[#E5E7EB] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#E21836]/20"
                    />
                  </div>
                  <button
                    data-testid="save-address-btn"
                    onClick={handleProceedToLocation}
                    className="w-full py-2.5 bg-[#111827] text-white text-sm font-semibold rounded-xl"
                  >
                    {googlePlaceLatLng ? 'Save Address' : 'Next: Confirm Location'}
                  </button>
                </motion.div>
              )}

              {showAddAddress && showLocationPicker && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-3">
                  <AddressLocationPicker
                    onConfirm={(lat, lng) => finalizeAddAddress(lat, lng)}
                    onSkip={() => finalizeAddAddress(null, null)}
                    addressText={[newAddress.address_line, newAddress.landmark, newAddress.city, newAddress.pincode].filter(Boolean).join(', ')}
                  />
                </motion.div>
              )}
            </div>
          )}

          {/* Mandatory location confirmation for addresses saved before the
              location picker existed — closes the gap where an unconfirmed
              address silently defaulted to free/zero-distance delivery. */}
          {needsLocationConfirm && (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[#E21836] mb-2">
                Confirm This Address's Location
              </h3>
              <p className="text-xs text-[#6B7280] mb-2">
                This saved address needs a one-time location confirmation before we can calculate delivery correctly.
              </p>
              <AddressLocationPicker
                key={selectedAddress?.id}
                onConfirm={(lat, lng) => handleConfirmExistingAddressLocation(lat, lng)}
                addressText={selectedAddress ? [selectedAddress.address_line, selectedAddress.landmark, selectedAddress.city, selectedAddress.pincode].filter(Boolean).join(', ') : ''}
              />
            </div>
          )}

          {/* Special Instructions */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-2">Special Instructions</h3>
            <textarea
              data-testid="special-instructions"
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              placeholder="Any special requests? (e.g., extra spicy, no onion)"
              rows={2}
              className="w-full px-3 py-2.5 bg-[#F8FAFC] border border-[#E5E7EB] rounded-xl text-sm text-[#111827] placeholder-[#6B7280]/60 focus:outline-none focus:ring-2 focus:ring-[#E21836]/20 resize-none"
            />
          </div>

          {/* Payment */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-2">Payment Method</h3>

            {wallet && wallet.redeemable_now >= Math.ceil((quote ? quote.total : subtotal / 100) * 2) && (
              <button
                data-testid="pay-with-points-toggle"
                onClick={() => { setPayWithPoints((v) => !v); setUseWallet(false); }}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border mb-2 transition-all ${
                  payWithPoints ? 'bg-[#E21836] border-[#E21836] text-white' : 'bg-white border-[#E5E7EB] text-[#111827]'
                }`}
              >
                <Coins size={22} weight="duotone" className={payWithPoints ? 'text-white' : 'text-[#E21836]'} />
                <div className="flex-1 text-left">
                  <span className="text-sm font-semibold">Pay with Points</span>
                  <p className={`text-[10px] ${payWithPoints ? 'text-white/80' : 'text-[#6B7280]'}`}>
                    You have {wallet.redeemable_now} points available
                  </p>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${payWithPoints ? 'border-white bg-white' : 'border-[#E5E7EB]'}`}>
                  {payWithPoints && <div className="w-2.5 h-2.5 rounded-full bg-[#E21836]" />}
                </div>
              </button>
            )}

            {!payWithPoints && walletBalance > 0 && (
              <button
                data-testid="use-wallet-toggle"
                onClick={() => setUseWallet((v) => !v)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border mb-2 transition-all ${
                  useWallet ? 'bg-[#4a6741] border-[#4a6741] text-white' : 'bg-white border-[#E5E7EB] text-[#111827]'
                }`}
              >
                <WalletIcon size={22} weight="duotone" className={useWallet ? 'text-white' : 'text-[#4a6741]'} />
                <div className="flex-1 text-left">
                  <span className="text-sm font-semibold">Use Wallet Credit</span>
                  <p className={`text-[10px] ${useWallet ? 'text-white/80' : 'text-[#6B7280]'}`}>
                    ₹{walletBalance.toFixed(2)} available — covers {' '}
                    {walletBalance >= (quote ? quote.total : subtotal) / 100 ? 'this order fully' : 'part of this order'}
                  </p>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${useWallet ? 'border-white bg-white' : 'border-[#E5E7EB]'}`}>
                  {useWallet && <div className="w-2.5 h-2.5 rounded-full bg-[#4a6741]" />}
                </div>
              </button>
            )}

            {!payWithPoints && (
              <div className="flex items-center gap-3 p-3 bg-[#E21836]/5 border border-[#E21836] rounded-xl">
                <CreditCard size={22} weight="duotone" className="text-[#E21836]" />
                <div className="flex-1">
                  <span className="text-sm font-semibold text-[#111827]">Pay Online</span>
                  <p className="text-[10px] text-[#6B7280]">UPI, Cards, Net Banking, Wallets</p>
                </div>
                <div className="flex items-center gap-1 text-[#2E7D32]">
                  <ShieldCheck size={14} weight="fill" />
                  <span className="text-[10px] font-semibold">Secured by Razorpay</span>
                </div>
              </div>
            )}
          </div>

          {/* Order Summary */}
          <div className="p-4 bg-[#F8FAFC] border border-[#E5E7EB] rounded-2xl">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-3">Order Summary</h3>
            {cart.map((item) => (
              <div key={item.signature} className="flex justify-between text-sm mb-1">
                <span className="text-[#6B7280]">{item.menu_item_name}{item.variant?.name ? ` (${item.variant.name})` : ''} x{item.qty}</span>
                <span className="text-[#111827]">{formatINR(engine.lineTotal(item))}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm mt-2 pt-2 border-t border-[#E5E7EB]">
              <span className="text-[#6B7280]">Subtotal</span>
              <span className="text-[#111827]">{formatINR(subtotal)}</span>
            </div>
            {orderType === 'DELIVERY' && (
              <div className="flex justify-between text-sm mt-1" data-testid="checkout-delivery-fee">
                <span className="text-[#6B7280]">Delivery fee</span>
                <span className="text-[#111827]">{quote ? (quote.delivery.free ? 'FREE' : `₹${quote.delivery_fee.toFixed(0)}`) : '—'}</span>
              </div>
            )}
            {quote && quote.tax > 0 && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-[#6B7280]">GST{quote.tax_percent ? ` (${quote.tax_percent}%)` : ''}</span>
                <span className="text-[#111827]">₹{quote.tax.toFixed(0)}</span>
              </div>
            )}
            <div className="border-t border-[#E5E7EB] mt-2 pt-2 flex justify-between">
              <span className="font-bold text-[#111827]">Total</span>
              <span className="font-bold text-[#E21836]" data-testid="checkout-total">₹{(quote ? quote.total : subtotal / 100).toFixed(0)}</span>
            </div>
            {orderType === 'DELIVERY' && quote && !quote.delivery.deliverable && (
              <p className="text-xs text-red-500 mt-2" data-testid="checkout-block-reason">{quote.delivery.reason}</p>
            )}
            {orderType === 'DELIVERY' && quote && quote.delivery.deliverable && !quote.delivery.min_order_met && (
              <p className="text-xs text-red-500 mt-2" data-testid="checkout-block-reason">Minimum order for delivery is ₹{quote.delivery.min_order_value.toFixed(0)}</p>
            )}
          </div>
        </div>

        {/* Pay & Place Order Button */}
        <div className="fixed bottom-20 left-4 right-4 z-40">
          <button
            data-testid="place-order-btn"
            onClick={handlePlaceOrder}
            disabled={loading || needsLocationConfirm || selectedStore?.accepting_orders === false || (orderType === 'DELIVERY' && quote && (!quote.delivery.deliverable || !quote.delivery.min_order_met))}
            className="w-full py-4 bg-[#111827] hover:bg-[#374151] disabled:opacity-50 text-white font-semibold rounded-2xl shadow-lg transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              'Processing...'
            ) : (
              <>
                {payWithPoints ? (
                  <><Coins size={16} weight="fill" /> {`Place Order with Points \u00b7 ${Math.ceil((quote ? quote.total : subtotal / 100) * 2)} pts`}</>
                ) : (
                  <><Lock size={16} weight="fill" /> {`Pay & Place Order \u00b7 ₹${(quote ? quote.total : subtotal / 100).toFixed(0)}`}</>
                )}
              </>
            )}
          </button>
        </div>
      </div>
    </PageWrap>
  );
}
